import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  UploadCloud, FileText, ArrowRight, Loader2, Check, AlertTriangle, X,
  ShoppingBag, Wand2, Package, ChevronRight, RefreshCw, Sparkles, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

const PLATFORMS = [
  { key: "woocommerce", label: "WooCommerce", icon: "🛍", color: "from-purple-500 to-indigo-600", hint: "Export CSV depuis WordPress → Outils → Exporter" },
  { key: "shopify", label: "Shopify", icon: "🏪", color: "from-emerald-500 to-green-600", hint: "Dashboard Shopify → Produits → Exporter en CSV" },
  { key: "auto", label: "Détection auto", icon: "✨", color: "from-sky-500 to-blue-600", hint: "Laisse le COCKPIT analyser les en-têtes de votre fichier" },
];

export default function ProductsImporterAdmin() {
  const fileRef = useRef(null);
  const [step, setStep] = useState(1);        // 1 = upload, 2 = mapping, 3 = running, 4 = done
  const [platform, setPlatform] = useState("auto");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [skipDup, setSkipDup] = useState(true);
  const [setUnpublished, setSetUnpublished] = useState(false);
  const [dryRun, setDryRun] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const onPickFile = async (f) => {
    if (!f) return;
    if (!/\.csv$/i.test(f.name)) { toast.error("Format non supporté : CSV uniquement pour l'instant"); return; }
    setFile(f);
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("platform", platform);
      const { data } = await api.post("/imports/products/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      setMapping(data.suggested_mapping || {});
      setStep(2);
      toast.success(`${data.total_rows} produits détectés · plateforme ${data.detected_platform}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de lire le fichier");
    } finally { setLoading(false); }
  };

  const runImport = async () => {
    setLoading(true); setStep(3);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mapping_json", JSON.stringify(mapping));
      fd.append("skip_duplicates", String(skipDup));
      fd.append("set_unpublished", String(setUnpublished));
      fd.append("dry_run", String(dryRun));
      const { data } = await api.post("/imports/products/run", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      setStep(4);
      if (!dryRun) toast.success(`${data.stats.created} produits importés, ${data.stats.skipped} ignorés`);
      else toast.info(`Simulation : ${data.stats.created} produits seraient importés`);
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
      setStep(2);
    } finally { setLoading(false); }
  };

  const reset = () => {
    setStep(1); setFile(null); setPreview(null); setMapping({}); setResult(null);
  };

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="products-importer">
      <header>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-[color:var(--brand-sky)] mb-1">
          <Wand2 className="w-3.5 h-3.5" /> Import de catalogue
        </div>
        <h1 className="font-display text-3xl font-bold text-[color:var(--brand-navy)]">Importer depuis WooCommerce ou Shopify</h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 max-w-2xl">
          Convertissez un site existant en quelques clics : exportez le CSV de vos produits, uploadez-le ici, le COCKPIT détecte automatiquement la plateforme et mappe les colonnes.
        </p>
      </header>

      {/* Stepper */}
      <div className="flex items-center gap-2 text-xs font-semibold">
        {[
          { n: 1, label: "Upload" },
          { n: 2, label: "Mapping" },
          { n: 3, label: "Import" },
          { n: 4, label: "Résultat" },
        ].map((s, i) => (
          <React.Fragment key={s.n}>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
              step === s.n ? "bg-[color:var(--brand-navy)] text-white"
                : step > s.n ? "bg-emerald-100 text-emerald-700"
                : "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"
            }`}>
              <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-[10px] font-bold">
                {step > s.n ? <Check className="w-3 h-3" /> : s.n}
              </span>
              {s.label}
            </div>
            {i < 3 && <ChevronRight className="w-3 h-3 text-[hsl(var(--muted-foreground))]" />}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1 — Upload */}
      {step === 1 && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {PLATFORMS.map(p => (
              <button
                key={p.key}
                type="button"
                onClick={() => setPlatform(p.key)}
                className={`p-4 rounded-xl text-left transition-all border-2 ${
                  platform === p.key
                    ? "border-[color:var(--brand-sky)] bg-[color:var(--brand-sky)]/5"
                    : "border-[hsl(var(--border))] hover:border-[color:var(--brand-sky)]/50"
                }`}
                data-testid={`platform-${p.key}`}
              >
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${p.color} flex items-center justify-center text-xl mb-2`}>
                  {p.icon}
                </div>
                <div className="font-bold text-[color:var(--brand-navy)]">{p.label}</div>
                <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">{p.hint}</p>
              </button>
            ))}
          </div>

          <Card
            className="p-10 border-2 border-dashed border-[hsl(var(--border))] text-center cursor-pointer hover:border-[color:var(--brand-sky)] transition-colors bg-gradient-to-br from-[color:var(--brand-sky)]/5 to-transparent"
            onClick={() => fileRef.current?.click()}
            onDragOver={e => { e.preventDefault(); }}
            onDrop={e => { e.preventDefault(); onPickFile(e.dataTransfer.files?.[0]); }}
            data-testid="upload-dropzone"
          >
            <input
              ref={fileRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={e => onPickFile(e.target.files?.[0])}
              data-testid="upload-input"
            />
            {loading ? (
              <Loader2 className="w-10 h-10 mx-auto animate-spin text-[color:var(--brand-sky)] mb-3" />
            ) : (
              <UploadCloud className="w-12 h-12 mx-auto text-[color:var(--brand-sky)] mb-3" />
            )}
            <div className="font-display font-bold text-lg text-[color:var(--brand-navy)] mb-1">
              {loading ? "Analyse en cours…" : "Glissez-déposez votre fichier CSV ici"}
            </div>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">ou cliquez pour parcourir · max 20 Mo · UTF-8 recommandé</p>
          </Card>

          <Card className="p-5 bg-amber-50 border-amber-200">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                <FileText className="w-4 h-4 text-amber-700" />
              </div>
              <div className="text-xs text-amber-900 space-y-1">
                <div className="font-bold text-sm">💡 Comment exporter mes produits ?</div>
                <div><strong>WooCommerce</strong> : WordPress → Produits → Tous les produits → cliquez "Exporter" en haut. Cochez "Toutes les colonnes" → Générer CSV.</div>
                <div><strong>Shopify</strong> : Admin → Produits → Exporter → "Tous les produits" → CSV pour Excel/tableurs.</div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Step 2 — Mapping */}
      {step === 2 && preview && (
        <div className="space-y-4">
          <Card className="p-4 bg-gradient-to-br from-emerald-50 to-transparent border-emerald-200">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-emerald-700" />
                </div>
                <div>
                  <div className="font-bold text-[color:var(--brand-navy)]">
                    Plateforme détectée : <span className="text-emerald-700">{preview.detected_platform}</span>
                  </div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))]">
                    {preview.total_rows} produits trouvés · {preview.headers.length} colonnes CSV
                  </div>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={reset} data-testid="importer-reset">
                <X className="w-3.5 h-3.5 mr-1" /> Changer de fichier
              </Button>
            </div>
          </Card>

          <Card className="p-5">
            <h2 className="font-display font-bold text-lg text-[color:var(--brand-navy)] mb-3">Correspondance des colonnes</h2>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mb-4">
              Associez chaque colonne de votre fichier à un champ du COCKPIT. Laissez vide pour ignorer.
            </p>
            <div className="space-y-2 max-h-[500px] overflow-auto pr-2">
              {preview.headers.map(h => (
                <div key={h} className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 py-2 border-b border-[hsl(var(--border))] last:border-b-0">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-bold">Source CSV</div>
                    <div className="font-mono text-sm font-bold text-[color:var(--brand-navy)] truncate" title={h}>{h}</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-[color:var(--brand-sky)]" />
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-bold">Champ COCKPIT</div>
                    <Select
                      value={mapping[h] || "__none__"}
                      onValueChange={v => setMapping({ ...mapping, [h]: v === "__none__" ? "" : v })}
                    >
                      <SelectTrigger className="h-8 text-xs" data-testid={`mapping-${h}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— Ignorer —</SelectItem>
                        {preview.cockpit_fields.map(f => (
                          <SelectItem key={f.key} value={f.key}>
                            {f.label} {f.required && "*"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {preview.sample_preview?.length > 0 && (
            <Card className="p-5">
              <h3 className="font-bold text-sm text-[color:var(--brand-navy)] mb-2">Aperçu · 3 premiers produits</h3>
              <div className="space-y-1.5">
                {preview.sample_preview.slice(0, 3).map((p, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-[hsl(var(--muted))]/50 text-xs">
                    <div className="w-10 h-10 rounded-lg bg-white overflow-hidden shrink-0">
                      {p.featured_image ? <img src={p.featured_image} className="w-full h-full object-cover" alt="" /> : <Package className="w-4 h-4 m-auto mt-3 text-[hsl(var(--muted-foreground))]" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{p.name}</div>
                      <div className="text-[11px] text-[hsl(var(--muted-foreground))]">SKU: {p.sku || "—"} · Prix: {((p.price_cents || 0) / 100).toFixed(2)}€ · Stock: {p.stock_qty || 0}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <Card className="p-5">
            <h3 className="font-bold text-sm text-[color:var(--brand-navy)] mb-3">Options d'import</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-2"><Switch checked={skipDup} onCheckedChange={setSkipDup} data-testid="opt-skip-dup" /><Label className="text-xs">Ignorer les produits déjà existants (même SKU, slug ou nom)</Label></div>
              <div className="flex items-center gap-2"><Switch checked={setUnpublished} onCheckedChange={setSetUnpublished} data-testid="opt-unpublished" /><Label className="text-xs">Importer en <strong>brouillon</strong> (non publiés) — vous pourrez les vérifier avant publication</Label></div>
              <div className="flex items-center gap-2"><Switch checked={dryRun} onCheckedChange={setDryRun} data-testid="opt-dry-run" /><Label className="text-xs">Mode simulation (ne rien créer, vérifier uniquement)</Label></div>
            </div>
          </Card>

          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" onClick={reset}>Annuler</Button>
            <Button onClick={runImport} disabled={loading} className="bg-[color:var(--brand-navy)]" data-testid="run-import-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
              {dryRun ? "Simuler l'import" : `Importer ${preview.total_rows} produits`}
            </Button>
          </div>
        </div>
      )}

      {/* Step 3 — Running */}
      {step === 3 && (
        <Card className="p-12 text-center">
          <Loader2 className="w-12 h-12 mx-auto text-[color:var(--brand-sky)] animate-spin mb-4" />
          <div className="font-display font-bold text-lg text-[color:var(--brand-navy)]">Import en cours…</div>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Création de vos produits dans le catalogue COCKPIT.</p>
        </Card>
      )}

      {/* Step 4 — Done */}
      {step === 4 && result && (
        <div className="space-y-4">
          <Card className={`p-6 border-2 ${result.stats.errors === 0 ? "bg-emerald-50 border-emerald-300" : "bg-amber-50 border-amber-300"}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${result.stats.errors === 0 ? "bg-emerald-500" : "bg-amber-500"}`}>
                <CheckCircle2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="font-display font-bold text-xl text-[color:var(--brand-navy)]">
                  {result.dry_run ? "Simulation terminée" : "Import terminé"}
                </h2>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">
                  {result.stats.created} créés · {result.stats.skipped} ignorés · {result.stats.errors} erreurs
                </p>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2 text-center">
              <Stat label="Total" value={result.stats.total} color="bg-slate-100 text-slate-700" />
              <Stat label="Créés" value={result.stats.created} color="bg-emerald-100 text-emerald-700" />
              <Stat label="Ignorés" value={result.stats.skipped} color="bg-blue-100 text-blue-700" />
              <Stat label="Erreurs" value={result.stats.errors} color="bg-red-100 text-red-700" />
            </div>
          </Card>

          {result.errors?.length > 0 && (
            <Card className="p-5">
              <h3 className="font-bold text-sm text-[color:var(--brand-navy)] mb-2 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-600" /> Erreurs détaillées</h3>
              <div className="space-y-1 text-xs max-h-60 overflow-auto">
                {result.errors.map((e, i) => (
                  <div key={i} className="flex gap-2 p-1.5 rounded bg-red-50 text-red-800">
                    <span className="font-mono shrink-0">Ligne {e.row}</span>
                    <span>{e.reason}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" onClick={reset}><RefreshCw className="w-4 h-4 mr-1.5" />Nouvel import</Button>
            <Link to="/admin/products">
              <Button className="bg-[color:var(--brand-navy)]" data-testid="see-products-btn">
                <ShoppingBag className="w-4 h-4 mr-1.5" />Voir les produits
              </Button>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className={`rounded-lg p-3 ${color}`}>
      <div className="text-[10px] uppercase tracking-wider font-bold opacity-70">{label}</div>
      <div className="font-display text-2xl font-black">{value}</div>
    </div>
  );
}
